/**
 * MazeFlowCanvas.tsx
 * 迷路アプリのフローチャート編集キャンバス。
 * 既存の FlowCanvas / nodeRegistry と全カードをそのまま再利用する。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Home, RotateCcw, Trash2 } from 'lucide-react';
import { useAtom } from 'jotai';
import {
  addEdge,
  ReactFlowProvider,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  type Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '@/flow/reactflow-overrides.css';
import { FlowCanvas } from '@/flow/components/FlowCanvas';
import { NodeSettingsDialog } from '@/flow/components/NodeSettingsDialog';
import { getNodeTypes, getDefaultData, getSettings } from '@/flow/nodeRegistry';
import { getInitialFunctionNodes } from '@/flow/config/fixedNodes';
import { getCurrentFlowLayoutPreset } from '@/flow/config/layoutPreset';
import {
  FlowDisplayLanguageProvider,
  getNodeDisplayLabel,
  type FlowDisplayLanguage,
} from '@/flow/displayLanguage';
import { functionWorkspaceRequestAtom } from '@/flow/atoms/functionWorkspaceAtom';
import type { AppEdge, AppNode, AppNodeOf, NodeKind } from '@/flow/types';
import { loadMazeFlow, saveMazeFlow } from './mazeFlowPersistence';
import {
  getFunctionWorkspaceFlow,
  materializeMazeWorkspace,
  type MazeWorkspaceFrame,
} from './mazeFunctionWorkspace';
import { createInitialMazeFlow } from './mazeFlowState';

const nodeTypes = getNodeTypes();
let nodeIdCounter = 0;
const nextNodeId = (kind: NodeKind) => {
  nodeIdCounter += 1;
  return `${kind}-${Date.now()}-${nodeIdCounter}`;
};

type MazeFlowCanvasInnerProps = {
  stageId: number;
  initialFlow?: { nodes: AppNode[]; edges: AppEdge[] };
  allowedNodeKinds?: readonly NodeKind[];
  onPersistFlow?: (nodes: AppNode[], edges: AppEdge[]) => void;
  ensureInitialFit?: boolean;
  onFlowChange: (nodes: AppNode[], edges: AppEdge[]) => void;
  addRequestKind: NodeKind | null;
  onAddHandled: () => void;
  activeNodeIds: ReadonlySet<string>;
  blockedNodeIds: ReadonlySet<string>;
  activeEdgeIds: ReadonlySet<string>;
};

function MazeFlowCanvasInner({ stageId, initialFlow, allowedNodeKinds, onPersistFlow, ensureInitialFit = false, onFlowChange, addRequestKind, onAddHandled, activeNodeIds, blockedNodeIds, activeEdgeIds }: MazeFlowCanvasInnerProps) {
  const preset = getCurrentFlowLayoutPreset();
  const restored = useMemo(() => initialFlow ?? loadMazeFlow(stageId), [initialFlow, stageId]);
  const defaultFlow = useMemo(() => createInitialMazeFlow(preset), [preset]);
  const [nodes, setNodes, onNodesChange] = useNodesState<AppNode>(restored?.nodes ?? defaultFlow.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<AppEdge>(restored?.edges ?? defaultFlow.edges);
  const [workspaceStack, setWorkspaceStack] = useState<MazeWorkspaceFrame[]>([]);
  const [functionWorkspaceRequest, setFunctionWorkspaceRequest] = useAtom(functionWorkspaceRequestAtom);
  const { fitView, screenToFlowPosition } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialFitDoneRef = useRef(false);
  const [settingsNodeId, setSettingsNodeId] = useState<string | null>(null);
  const closeSettings = useCallback(() => setSettingsNodeId(null), []);
  const settingsNode = nodes.find((node) => node.id === settingsNodeId) ?? null;
  const settingsContent = settingsNode ? getSettings(settingsNode.type) : undefined;
  const selectedNodeIds = nodes
    .filter((node) => node.selected && node.id !== 'start' && node.id !== 'end')
    .map((node) => node.id);
  const selectedEdgeIds = edges.filter((edge) => edge.selected).map((edge) => edge.id);
  const canDeleteSelection = selectedNodeIds.length > 0 || selectedEdgeIds.length > 0;
  const displayNodes = useMemo(() => nodes.map((node) => {
    const className = [
      node.className,
      activeNodeIds.has(node.id) ? 'executing' : null,
      blockedNodeIds.has(node.id) ? 'blocked' : null,
    ].filter(Boolean).join(' ');
    return className === (node.className ?? '') ? node : { ...node, className };
  }), [activeNodeIds, blockedNodeIds, nodes]);
  const displayEdges = useMemo(() => edges.map((edge) => (
    activeEdgeIds.has(edge.id)
      ? { ...edge, className: [edge.className, 'executing'].filter(Boolean).join(' ') }
      : edge
  )), [activeEdgeIds, edges]);

  useEffect(() => {
    if (!ensureInitialFit || !nodesInitialized || initialFitDoneRef.current) return;
    initialFitDoneRef.current = true;
    const frame = window.requestAnimationFrame(() => {
      void fitView({ padding: 0.12 });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [ensureInitialFit, fitView, nodesInitialized]);

  useEffect(() => {
    const rootFlow = materializeMazeWorkspace(workspaceStack, nodes, edges);
    onFlowChange(rootFlow.nodes, rootFlow.edges);
    if (onPersistFlow) onPersistFlow(rootFlow.nodes, rootFlow.edges);
    else saveMazeFlow(stageId, rootFlow.nodes, rootFlow.edges);
  }, [nodes, edges, stageId, onFlowChange, onPersistFlow, workspaceStack]);

  useEffect(() => {
    if (!functionWorkspaceRequest) return;
    const functionNode = nodes.find((node): node is AppNodeOf<'function'> => (
      node.id === functionWorkspaceRequest && node.type === 'function'
    ));
    if (!functionNode) return;

    setFunctionWorkspaceRequest(null);
    const functionName = functionNode.data.functionName.trim();
    if (!functionName) return;
    const nextFlow = getFunctionWorkspaceFlow(
      functionNode,
      () => getInitialFunctionNodes(preset) as AppNode[],
    );
    setWorkspaceStack((current) => current.concat({
      functionNodeId: functionNode.id,
      functionName,
      nodes,
      edges,
    }));
    setNodes(nextFlow.nodes);
    setEdges(nextFlow.edges);
    setSettingsNodeId(null);
  }, [
    edges,
    functionWorkspaceRequest,
    nodes,
    preset,
    setEdges,
    setFunctionWorkspaceRequest,
    setNodes,
  ]);

  const returnToRootWorkspace = useCallback(() => {
    if (workspaceStack.length === 0) return;
    const rootFlow = materializeMazeWorkspace(workspaceStack, nodes, edges);
    setWorkspaceStack([]);
    setNodes(rootFlow.nodes);
    setEdges(rootFlow.edges);
    setSettingsNodeId(null);
  }, [edges, nodes, setEdges, setNodes, workspaceStack]);

  const onConnect = useCallback((connection: Connection) => {
    setEdges((eds) => addEdge(connection, eds));
  }, [setEdges]);

  const addNode = useCallback((kind: NodeKind, position: { x: number; y: number }) => {
    if (allowedNodeKinds && !allowedNodeKinds.includes(kind)) return;
    const newNode = {
      id: nextNodeId(kind),
      type: kind,
      position,
      data: getDefaultData(kind),
    } as AppNode;
    setNodes((current) => [...current, newNode]);
  }, [allowedNodeKinds, setNodes]);

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const kind = event.dataTransfer.getData('application/node-kind') as NodeKind;
    if (!kind) return;
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    addNode(kind, position);
  }, [addNode, screenToFlowPosition]);

  useEffect(() => {
    if (!addRequestKind) return;
    addNode(addRequestKind, { x: 200 + Math.random() * 80, y: 260 + Math.random() * 80 });
    onAddHandled();
  }, [addRequestKind, addNode, onAddHandled]);

  const deleteSelection = useCallback(() => {
    if (!canDeleteSelection) return;
    const nodeIdSet = new Set(selectedNodeIds);
    const edgeIdSet = new Set(selectedEdgeIds);
    setNodes((current) => current.filter((node) => !nodeIdSet.has(node.id)));
    setEdges((current) => current.filter((edge) => (
      !edgeIdSet.has(edge.id)
      && !nodeIdSet.has(edge.source)
      && !nodeIdSet.has(edge.target)
    )));
    setSettingsNodeId((current) => current && nodeIdSet.has(current) ? null : current);
  }, [canDeleteSelection, selectedEdgeIds, selectedNodeIds, setEdges, setNodes]);

  const resetFlow = useCallback(() => {
    const reset = createInitialMazeFlow(preset);
    setWorkspaceStack([]);
    setNodes(reset.nodes);
    setEdges(reset.edges);
    setSettingsNodeId(null);
    setFunctionWorkspaceRequest(null);
  }, [preset, setEdges, setFunctionWorkspaceRequest, setNodes]);

  return (
    <>
      <FlowCanvas
        nodes={displayNodes}
        edges={displayEdges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }}
        onDrop={onDrop}
        onNodeClick={(event, node) => {
          if (event.target instanceof Element && event.target.closest('[data-node-settings]') && getSettings(node.type)) {
            setSettingsNodeId(node.id);
          }
        }}
        onNodeDoubleClick={() => undefined}
        onPaneClick={() => undefined}
        onEdgeContextMenu={(event) => event.preventDefault()}
        onNodeContextMenu={(event) => event.preventDefault()}
        viewportPreset={preset}
        containerRef={containerRef}
      >
        {workspaceStack.length > 0 && (
          <div className="function-workspace-badge" aria-live="polite">
            関数ワークスペース: {workspaceStack[workspaceStack.length - 1].functionName}
          </div>
        )}
        {workspaceStack.length > 0 && (
          <nav className="function-breadcrumbs" aria-label="作業場の階層">
            <button type="button" onClick={returnToRootWorkspace} className="function-breadcrumbs__root">
              <Home aria-hidden="true" size={15} />ルート
            </button>
            {workspaceStack.map((frame) => (
              <span key={frame.functionNodeId} className="function-breadcrumbs__item">
                <ChevronRight aria-hidden="true" size={14} />
                {frame.functionName}
              </span>
            ))}
          </nav>
        )}
        <div className="maze-flow-actions">
          <button
            type="button"
            className="maze-flow-reset-button"
            onClick={resetFlow}
            aria-label="フローチャートを初期化"
            title="フローチャートを初期化"
          >
            <RotateCcw aria-hidden="true" size={17} />
            <span>初期化</span>
          </button>
          <button
            type="button"
            className="maze-flow-delete-button"
            disabled={!canDeleteSelection}
            onClick={deleteSelection}
            aria-label="選択したNodeまたは線を削除"
            title="選択したNodeまたは線を削除"
          >
            <Trash2 aria-hidden="true" size={17} />
            <span>削除</span>
          </button>
        </div>
      </FlowCanvas>
      {settingsNode && settingsContent && (
        <NodeSettingsDialog
          node={settingsNode}
          title={getNodeDisplayLabel(settingsNode.type, 'ja')}
          content={settingsContent}
          onClose={closeSettings}
        />
      )}
    </>
  );
}

type MazeFlowCanvasProps = {
  displayLanguage?: FlowDisplayLanguage;
  stageId: number;
  initialFlow?: { nodes: AppNode[]; edges: AppEdge[] };
  allowedNodeKinds?: readonly NodeKind[];
  onPersistFlow?: (nodes: AppNode[], edges: AppEdge[]) => void;
  ensureInitialFit?: boolean;
  onFlowChange: (nodes: AppNode[], edges: AppEdge[]) => void;
  addRequestKind: NodeKind | null;
  onAddHandled: () => void;
  activeNodeIds: ReadonlySet<string>;
  blockedNodeIds: ReadonlySet<string>;
  activeEdgeIds: ReadonlySet<string>;
};

export function MazeFlowCanvas(props: MazeFlowCanvasProps) {
  return (
    <FlowDisplayLanguageProvider value={props.displayLanguage ?? 'ja'}>
      <ReactFlowProvider>
        <MazeFlowCanvasInner {...props} />
      </ReactFlowProvider>
    </FlowDisplayLanguageProvider>
  );
}
